// Shared revision/clarification thread query, threaded across the design loops
// (redesign + refinement). Phase differences are passed in as config.

import { useQuery, type QueryClient } from '@tanstack/react-query';
import { fetchTimeline } from '../../../services/timeline';
import type { TimelineEvent } from '../../patient-files/actions/types';
import { TIMELINE_HISTORY_STALE_MS, tiptapDocToPlainText, type TiptapDoc } from '@odo/shared-ui';

export type ConversationKind =
    'revision' | 'resubmission' | 'clarification-requested' | 'clarification-provided';

export interface ConversationEntry {
    id: string;
    createdAt: string;
    author: string;
    text: string;
    kind: ConversationKind;
    // Replaces the kind's badge label, e.g. for a revision the orthodontist requested.
    label?: string;
}

interface KindMeta {
    label: string;
    badgeBg: string;
    badgeText: string;
}

// Older revision events carry no actorRole; they keep the kind's (or override's) label.
const REVISION_LABEL_BY_ACTOR_ROLE: Record<string, string> = {
    gp: 'GP requested revision',
    ortho: 'Orthodontist requested revision'
};

export const DEFAULT_KIND_META: Record<ConversationKind, KindMeta> = {
    revision: { label: 'Revision request', badgeBg: 'bg-yellow-100', badgeText: 'text-yellow-800' },
    resubmission: { label: 'ODO resubmitted', badgeBg: 'bg-blue-100', badgeText: 'text-blue-800' },
    'clarification-requested': {
        label: 'ODO asked',
        badgeBg: 'bg-purple-100',
        badgeText: 'text-purple-800'
    },
    'clarification-provided': {
        label: 'Doctor responded',
        badgeBg: 'bg-emerald-100',
        badgeText: 'text-emerald-800'
    }
};

function extractEntry(
    event: TimelineEvent,
    kind: ConversationKind,
    field: string,
    docField?: string
): ConversationEntry | null {
    const payload = event.payload as Record<string, unknown> | undefined;
    const plain = typeof payload?.[field] === 'string' ? (payload[field] as string).trim() : '';
    const doc = docField ? (payload?.[docField] as TiptapDoc | undefined) : undefined;
    const text = plain || (doc ? tiptapDocToPlainText(doc).trim() : '');
    if (!text) return null;
    return {
        id: event.id,
        createdAt: event.createdAt,
        author: event.createdByUser?.name ?? event.createdBy ?? 'Doctor',
        text,
        kind
    };
}

function revisionEntry(event: TimelineEvent): ConversationEntry | null {
    const entry = extractEntry(event, 'revision', 'reason');
    const actorRole = (event.payload as { actorRole?: unknown } | undefined)?.actorRole;
    const label =
        typeof actorRole === 'string' ? REVISION_LABEL_BY_ACTOR_ROLE[actorRole] : undefined;
    return entry && label ? { ...entry, label } : entry;
}

export interface ClarificationHistoryConfig {
    /** Timeline event type for the revision request (GP or orthodontist). */
    revisionEventType: string;
    /** Timeline event type(s) whose `payload.designPlan` counts as a resubmission. */
    resubmissionEventTypes?: string[];
    /** Only entries created at/after this instant are included (scopes to the current cycle). */
    since?: string | null;
}

export interface ClarificationHistoryResult {
    entries: ConversationEntry[];
    revisionCount: number;
    isLoading: boolean;
    isError: boolean;
    isFetching: boolean;
    refetch: () => void;
}

// `queryKeyPrefix` keeps each phase's cache independent even though they share
// this hook (e.g. 'redesign-conversation' vs 'refinement-design-conversation').
export function useClarificationHistory(
    queryKeyPrefix: string,
    patientId: string,
    { revisionEventType, resubmissionEventTypes = [], since = null }: ClarificationHistoryConfig
): ClarificationHistoryResult {
    const { data, isLoading, isError, isFetching, refetch } = useQuery({
        queryKey: [queryKeyPrefix, patientId, revisionEventType, resubmissionEventTypes, since],
        queryFn: async (): Promise<ConversationEntry[]> => {
            const [revisions, resubmissionResults, asks, answers] = await Promise.all([
                fetchTimeline(patientId, { type: revisionEventType, limit: 50 }),
                Promise.all(
                    resubmissionEventTypes.map((type) =>
                        fetchTimeline(patientId, { type, limit: 50 })
                    )
                ),
                fetchTimeline(patientId, { type: 'clarification-requested', limit: 50 }),
                fetchTimeline(patientId, { type: 'clarification-provided', limit: 50 })
            ]);
            const sinceMs = since ? new Date(since).getTime() : null;
            const merged = [
                ...revisions.data.map(revisionEntry),
                ...resubmissionResults.flatMap((r) =>
                    r.data.map((e) => extractEntry(e, 'resubmission', 'designPlan'))
                ),
                ...asks.data.map((e) =>
                    extractEntry(e, 'clarification-requested', 'reason', 'reasonDoc')
                ),
                ...answers.data.map((e) => extractEntry(e, 'clarification-provided', 'response'))
            ].filter((x): x is ConversationEntry => x !== null);
            return merged
                .filter((x) => sinceMs === null || new Date(x.createdAt).getTime() >= sinceMs)
                .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        },
        staleTime: TIMELINE_HISTORY_STALE_MS,
        enabled: Boolean(patientId),
        // The doctor's answer arrives out of band - refetch on refocus so a
        // clinician tabbing back sees it without a manual reload.
        refetchOnWindowFocus: true
    });

    const entries = data ?? [];
    return {
        entries,
        revisionCount: entries.filter((e) => e.kind === 'revision').length,
        isLoading,
        isError,
        isFetching,
        refetch: () => void refetch()
    };
}

// Invalidates every clarification/revision thread for this patient via the
// shared "-conversation" queryKeyPrefix suffix, so a new phase needs no update.
export function invalidateClarificationHistory(
    queryClient: QueryClient,
    patientId: string
): Promise<void> {
    return queryClient.invalidateQueries({
        predicate: (query) => {
            const [prefix, id] = query.queryKey;
            return (
                typeof prefix === 'string' && prefix.endsWith('-conversation') && id === patientId
            );
        }
    });
}
